import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiSetPolicyRenewalFlagMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { NonRenewalReasonEnum, RenewalFlagEnum } from '@/lib/heyapi/types.gen';

import { formatErrorMessage } from '../../../utils';
import type { Message } from '../../StandardMessage';

const nonRenewalReasonLabels: Record<NonRenewalReasonEnum, string> = {
  [NonRenewalReasonEnum.CLAIMS_FREQUENCY]: 'Claims Frequency',
  [NonRenewalReasonEnum.CLAIM_SEVERITY]: 'Claim Severity',
  [NonRenewalReasonEnum.CLAIMS_FREQUENCY_AND_SEVERITY]:
    'Claims Frequency and Severity',
  [NonRenewalReasonEnum.NATURE_OF_OPERATIONS]: 'Nature of Operations',
  [NonRenewalReasonEnum.EXPOSURE_NO_LONGER_ACCEPTABLE]:
    'Exposure No Longer Acceptable',
  [NonRenewalReasonEnum.BOARD_ACTIONS]: 'Board Actions',
};

export const MarkAsNonRenew = ({
  accountId,
  isOpen,
  onOpenChange,
  setMessage,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
}) => {
  const queryClient = useQueryClient();
  const [selectedReason, setSelectedReason] = useState<
    NonRenewalReasonEnum | undefined
  >(undefined);

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const { mutate: markAsNonRenewal, isPending: isMarkingAsNonRenewal } =
    useMutation({
      ...glacierPolicyApiSetPolicyRenewalFlagMutation(),
      onSuccess() {
        setMessage(undefined);
        queryClient.refetchQueries({ queryKey: policyQK });
        onOpenChange(false);
        toast({
          title: 'Policy Marked as Non-Renew',
          description: (
            <div>
              <p>Policy marked as non-renew successfully</p>
            </div>
          ),
        });
      },
      onError(error) {
        setMessage(undefined);
        onOpenChange(false);
        toast({
          title: 'Error Marking Policy as Non-Renew',
          description: formatErrorMessage(
            error,
            'An error occurred while marking the policy as non-renew'
          ),
        });
      },
    });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as Non-Renew</DialogTitle>
          <DialogDescription>
            Select a reason for marking this account as non-renew. This will
            prevent the system from automatically creating a renewal for this
            account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Non-Renewal Reason</Label>
            <Select
              value={selectedReason ?? ''}
              onValueChange={(value) =>
                setSelectedReason(value as NonRenewalReasonEnum)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(NonRenewalReasonEnum).map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {nonRenewalReasonLabels[reason]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMarkingAsNonRenewal}
          >
            Cancel
          </Button>
          <Button
            disabled={isMarkingAsNonRenewal || !selectedReason}
            loading={isMarkingAsNonRenewal}
            onClick={() => {
              setMessage({
                content: 'Marking policy as non-renew...',
                level: 'INFO',
              });
              markAsNonRenewal({
                body: {
                  reasonForNonRenewal: selectedReason,
                },
                path: {
                  account_id: accountId,
                  flag: RenewalFlagEnum.FLAG_FOR_NON_RENEWAL,
                },
              });
            }}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
